// Global Design Tokens for DQH/MUI Components

// ===========================================
// TYPOGRAPHY
// ===========================================
export const FONT_SIZES = {
  // Headings
  h1: '2rem', // 32px
  h2: '1.5rem', // 24px
  h3: '1.25rem', // 20px
  h4: '1.125rem', // 18px
  h5: '1rem', // 16px
  h6: '0.9375rem', // 15px

  // Body text
  body1: '0.9375rem', // 15px - primary body text
  body2: '0.875rem', // 14px - secondary body text

  // UI elements
  button: '0.9375rem', // 15px
  input: '0.9375rem', // 15px
  label: '0.875rem', // 14px
  caption: '0.8125rem', // 13px
  overline: '0.75rem', // 12px

  // Table
  tableHeader: '0.8125rem', // 13px
  tableCell: '0.9rem', // 14.4px

  // Code/Mono
  code: '0.9rem', // 14.4px

  // Chips
  chipSmall: '0.8125rem', // 13px
  chipMedium: '0.875rem', // 14px

  // Stats/Numbers
  statLarge: '1.5rem', // 24px
  statMedium: '1.25rem', // 20px
};

export const FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  semiBold: 600,
  bold: 700,
};

// ===========================================
// SPACING
// ===========================================
export const SPACING = {
  xs: 0.5, // 4px
  sm: 1, // 8px
  md: 1.5, // 12px
  lg: 2, // 16px
  xl: 3, // 24px
  xxl: 4, // 32px
};

// ===========================================
// COLORS
// ===========================================
export const COLORS = {
  // Primary
  primary: {
    main: '#3B82F6',
    light: '#F0F9FF',
    dark: '#2563EB',
    contrastText: '#FFFFFF',
  },

  // Secondary
  secondary: {
    main: '#64748B',
    light: '#F1F5F9',
    dark: '#475569',
  },

  // Success
  success: {
    main: '#22C55E',
    light: '#F0FDF4',
    dark: '#16A34A',
  },

  // Error
  error: {
    main: '#EF4444',
    light: '#FEF2F2',
    dark: '#DC2626',
  },

  // Warning
  warning: {
    main: '#F59E0B',
    light: '#FFFBEB',
    dark: '#D97706',
  },

  // Text
  text: {
    primary: '#1E293B',
    secondary: '#64748B',
    disabled: '#94A3B8',
    hint: '#9CA3AF',
  },

  // Background
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
    subtle: '#F1F5F9',
  },

  // Borders
  border: {
    light: '#E2E8F0',
    main: '#CBD5E1',
    dark: '#94A3B8',
  },

  // AI / generative features (ingestion AI mapping, generated fields, etc.)
  ai: {
    main: '#7C3AED',
    light: '#F5F3FF',
    dark: '#6D28D9',
  },
};

/**
 * Filled Alert / global toast (Snackbar) colors.
 * MUI’s default filled Alert uses getContrastText(main); bright success.main reads as black.
 * These tokens are the single source for filled semantic bars + light foreground text.
 */
export const TOAST_FILLED_ALERT = {
  success: {
    background: COLORS.success.dark,
    color: '#FFFFFF',
  },
  error: {
    background: COLORS.error.main,
    color: '#FFFFFF',
  },
  warning: {
    background: COLORS.warning.dark,
    color: '#FFFFFF',
  },
  info: {
    background: COLORS.primary.main,
    color: COLORS.primary.contrastText,
  },
};

// ===========================================
// STACKING — MUI overlays above Carbon shell (see tutorial-header.scss ~9200–9300)
// ===========================================
export const Z_INDEX = {
  muiTooltip: 10000,
};

// ===========================================
// APP HEADER & SIDEBAR (TutorialHeader / shell)
// Professional, accessible palette: light header, dark sidebar
// ===========================================
export const HEADER = {
  // Light header bar: white/off-white background, dark text for readability
  background: '#FFFFFF',
  hover: '#F1F5F9',
  border: '#E2E8F0',
  text: '#0F172A',
  textSecondary: '#475569',
};

export const SIDEBAR = {
  // Dark sidebar: professional navy/slate, white text for contrast
  background: '#1E293B',
  selected: '#0f62fe',
  hover: '#334155',
  text: '#FFFFFF',
  textMuted: 'rgba(255, 255, 255, 0.85)',
  border: 'rgba(255, 255, 255, 0.08)',
  overlay: 'rgba(15, 23, 42, 0.6)',
  // Brand logo container (sits at top of sidebar)
  brandBg: '#FFFFFF',
};

// ===========================================
// SERVICE COLORS (for DP Services)
// ===========================================
export const SERVICE_COLORS = {
  'Name Parse': { bg: '#F5F7FF', border: '#6366F1', tag: '#6366F1' },
  'Email Hygiene': { bg: '#F0F9FF', border: '#3B82F6', tag: '#3B82F6' },
  'ZIP+4®': { bg: '#F0FDF4', border: '#22C55E', tag: '#16A34A' },
  'DPV®': { bg: '#ECFEFF', border: '#06B6D4', tag: '#0891B2' },
  'LACSLink®': { bg: '#F0FDFA', border: '#14B8A6', tag: '#0D9488' },
  IDMSMatchCodes: { bg: '#FAF5FF', border: '#A855F7', tag: '#9333EA' },
  'Cultural Code': { bg: '#FDF4FF', border: '#D946EF', tag: '#C026D3' },
  'Enhanced Deceased': { bg: '#FEF2F2', border: '#EF4444', tag: '#DC2626' },
  'Geographic Enhancement': {
    bg: '#FFF7ED',
    border: '#F97316',
    tag: '#EA580C',
  },
  'Consumer Enhancement': { bg: '#FFFBEB', border: '#F59E0B', tag: '#D97706' },
  'Reverse Email': { bg: '#F5F3FF', border: '#8B5CF6', tag: '#7C3AED' },
  WirelessMatchService: { bg: '#FEF3C7', border: '#EAB308', tag: '#CA8A04' },
  'Telephone Enhancement': { bg: '#E0F2FE', border: '#0EA5E9', tag: '#0284C7' },
  'Reverse Telephone': { bg: '#DBEAFE', border: '#2563EB', tag: '#1D4ED8' },
  Suppression: { bg: '#FEF2F2', border: '#FCA5A5', tag: '#DC2626' },
  EMLSUP: { bg: '#ECFEFF', border: '#67E8F9', tag: '#0891B2' },
  'B2C Link': { bg: '#FCE7F3', border: '#F9A8D4', tag: '#DB2777' },
};

// Default color for services not in the list - use a soft teal
export const DEFAULT_SERVICE_COLOR = {
  bg: '#F0FDFA',
  border: '#99F6E4',
  tag: '#0D9488',
};

export const getServiceColor = serviceName => {
  return SERVICE_COLORS[serviceName] || DEFAULT_SERVICE_COLOR;
};

// ===========================================
// COMPONENT DIMENSIONS
// ===========================================
export const DIMENSIONS = {
  // Chips
  chipHeight: {
    small: 26,
    medium: 32,
  },

  // Icons
  iconSize: {
    small: 18,
    medium: 22,
    large: 26,
  },

  // Color indicators
  colorDot: {
    small: 8,
    medium: 10,
    large: 12,
  },

  // Border radius
  borderRadius: {
    small: 0.5, // 4px
    medium: 1, // 8px
    large: 2, // 16px
  },
};

// ===========================================
// COMMON STYLES
// ===========================================
export const COMMON_STYLES = {
  // Paper/Card styles
  paper: {
    border: `1px solid ${COLORS.border.light}`,
    borderRadius: DIMENSIONS.borderRadius.medium,
    backgroundColor: COLORS.background.paper,
  },

  // Panel header
  panelHeader: {
    p: SPACING.lg,
    borderBottom: `1px solid ${COLORS.border.light}`,
    backgroundColor: COLORS.background.default,
  },

  // Panel header title
  panelHeaderTitle: {
    fontWeight: FONT_WEIGHTS.semiBold,
    color: COLORS.text.primary,
    fontSize: FONT_SIZES.h5,
  },

  // Table header cell
  tableHeaderCell: {
    backgroundColor: COLORS.background.default,
    fontWeight: FONT_WEIGHTS.semiBold,
    color: COLORS.text.secondary,
    fontSize: FONT_SIZES.tableHeader,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },

  // Table body cell
  tableBodyCell: {
    fontSize: FONT_SIZES.tableCell,
    py: SPACING.md,
  },

  // Code text
  codeText: {
    fontFamily: '"IBM Plex Mono", "SF Mono", Monaco, monospace',
    fontSize: FONT_SIZES.code,
    color: COLORS.text.primary,
    backgroundColor: 'rgba(255, 255, 255, 0.6)',
    px: 1,
    py: 0.5,
    borderRadius: DIMENSIONS.borderRadius.small,
  },

  // Clickable list item
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: SPACING.md,
    p: SPACING.md,
    borderRadius: DIMENSIONS.borderRadius.small,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: COLORS.background.default,
    },
  },

  // Selected list item
  listItemSelected: {
    backgroundColor: COLORS.primary.light,
    border: `1px solid ${COLORS.primary.main}`,
  },

  // Stat display
  statNumber: {
    fontWeight: FONT_WEIGHTS.semiBold,
    color: COLORS.text.primary,
    fontSize: FONT_SIZES.statLarge,
    lineHeight: 1,
  },

  statLabel: {
    color: COLORS.text.secondary,
    fontSize: FONT_SIZES.body2,
  },
};

// ===========================================
// CHIP STYLES
// ===========================================
export const getChipStyles = (color, size = 'small') => ({
  backgroundColor: color?.bg || COLORS.background.subtle,
  color: color?.tag || COLORS.text.secondary,
  border: `1px solid ${color?.border || COLORS.border.main}`,
  fontSize: size === 'small' ? FONT_SIZES.chipSmall : FONT_SIZES.chipMedium,
  height: DIMENSIONS.chipHeight[size],
  fontWeight: FONT_WEIGHTS.medium,
});

// ===========================================
// TABLE ROW STYLES (for service-colored rows)
// ===========================================
export const getServiceRowStyles = color => ({
  backgroundColor: color?.bg || COLORS.background.paper,
  borderLeft: `4px solid ${color?.tag || COLORS.border.main}`,
  '&:hover': {
    filter: 'brightness(0.97)',
  },
  '& td': {
    backgroundColor: 'inherit',
    py: SPACING.md,
  },
});

// ===========================================
// SELECTABLE CARD/TILE STYLES
// ===========================================
export const CARD_STYLES = {
  // Base selectable card
  selectableCard: {
    p: 3,
    border: `1px solid ${COLORS.border.light}`,
    borderRadius: 2,
    backgroundColor: COLORS.background.paper,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: COLORS.primary.main,
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.08)',
    },
  },

  // Selected state for selectable card
  selectableCardSelected: {
    borderColor: COLORS.primary.main,
    borderWidth: 2,
    boxShadow: '0 4px 16px rgba(59, 130, 246, 0.12)',
    backgroundColor: '#FAFBFF',
  },

  // Icon container for cards (rounded square with colored background)
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 2,
    backgroundColor: COLORS.primary.light,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  // Icon inside container
  cardIcon: {
    fontSize: 24,
    color: COLORS.primary.main,
  },

  // Card title
  cardTitle: {
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.h4,
    color: COLORS.text.primary,
    mb: 0.5,
  },

  // Card description
  cardDescription: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.secondary,
    lineHeight: 1.5,
  },
};

// ===========================================
// INFO BOX STYLES (for informational alerts)
// ===========================================
export const INFO_BOX_STYLES = {
  // Container
  container: {
    p: 2.5,
    backgroundColor: '#F0F9FF',
    borderRadius: 2,
    border: 'none',
    position: 'relative',
  },

  // Title with icon
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mb: 1.5,
    color: COLORS.primary.main,
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.body1,
  },

  // Title icon
  titleIcon: {
    fontSize: 20,
    color: COLORS.primary.main,
  },

  // Description text
  description: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.secondary,
    lineHeight: 1.6,
    mb: 1.5,
  },

  // List container
  list: {
    pl: 2.5,
    m: 0,
    '& li': {
      fontSize: FONT_SIZES.body2,
      color: COLORS.text.secondary,
      lineHeight: 1.8,
      '&::marker': {
        color: COLORS.primary.main,
      },
    },
  },

  // Close button
  closeButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    color: COLORS.text.secondary,
  },
};

// ===========================================
// SECTION STYLES
// ===========================================
export const SECTION_STYLES = {
  // Section header
  header: {
    mb: 1,
  },

  // Section title
  title: {
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.h5,
    color: COLORS.text.primary,
  },

  // Section subtitle/description
  subtitle: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.secondary,
    mt: 0.5,
  },
};

// ===========================================
// CHECKBOX/ACKNOWLEDGMENT STYLES
// ===========================================
export const CHECKBOX_STYLES = {
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mt: 2,
    p: 1.5,
    backgroundColor: COLORS.background.subtle,
    borderRadius: 1.5,
    border: `1px solid ${COLORS.border.light}`,
  },

  label: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.primary,
  },
};

// ===========================================
// SIDEBAR/STEPPER STYLES
// ===========================================
export const SIDEBAR_STYLES = {
  container: {
    width: 240,
    backgroundColor: COLORS.background.paper,
    borderRight: `1px solid ${COLORS.border.light}`,
    p: 3,
  },

  progressTitle: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.secondary,
    mb: 0.5,
  },

  progressBar: {
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.border.light,
    mb: 3,
  },

  stepItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1.5,
    py: 1.5,
    pl: 0.5,
    position: 'relative',
  },

  stepIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },

  stepIconCompleted: {
    backgroundColor: COLORS.success.main,
    color: COLORS.background.paper,
  },

  stepIconActive: {
    backgroundColor: COLORS.primary.main,
    color: COLORS.background.paper,
  },

  stepIconPending: {
    backgroundColor: COLORS.background.subtle,
    color: COLORS.text.disabled,
    border: `1px solid ${COLORS.border.main}`,
  },

  stepTitle: {
    fontWeight: FONT_WEIGHTS.medium,
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.primary,
  },

  stepDescription: {
    fontSize: FONT_SIZES.caption,
    color: COLORS.text.secondary,
    mt: 0.25,
  },

  stepConnector: {
    position: 'absolute',
    left: 11,
    top: 36,
    width: 2,
    height: 'calc(100% - 24px)',
    backgroundColor: COLORS.border.light,
  },
};

// ===========================================
// HELPER FUNCTIONS FOR SELECTABLE CARDS
// ===========================================
export const getSelectableCardStyles = isSelected => ({
  ...CARD_STYLES.selectableCard,
  ...(isSelected ? CARD_STYLES.selectableCardSelected : {}),
});

export const getIconContainerStyles = (color = COLORS.primary) => ({
  ...CARD_STYLES.iconContainer,
  backgroundColor: color.light || COLORS.primary.light,
  '& svg': {
    color: color.main || COLORS.primary.main,
  },
});

// ===========================================
// FORM SECTION STYLES
// ===========================================
export const FORM_SECTION_STYLES = {
  // Section container
  container: {
    mb: 4,
  },

  // Section title (e.g., "File Pickup", "Inbound Source")
  title: {
    fontWeight: FONT_WEIGHTS.semiBold,
    fontSize: FONT_SIZES.h5,
    color: COLORS.text.primary,
    mb: 0.5,
  },

  // Section description/subtitle
  description: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.secondary,
    mb: 2.5,
    lineHeight: 1.5,
  },

  // Row of fields (side by side, responsive)
  fieldRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 2,
    mb: 2,
  },
};

// ===========================================
// INPUT FIELD STYLES
// ===========================================
export const INPUT_STYLES = {
  // TextField styling
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: COLORS.background.paper,
      borderRadius: 1.5,
      fontSize: FONT_SIZES.body1,
      '& fieldset': {
        borderColor: COLORS.border.light,
        borderWidth: 1,
      },
      '&:hover fieldset': {
        borderColor: COLORS.border.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: COLORS.primary.main,
        borderWidth: 2,
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: FONT_SIZES.body2,
      color: COLORS.text.secondary,
      fontWeight: FONT_WEIGHTS.medium,
      '&.Mui-focused': {
        color: COLORS.primary.main,
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: FONT_SIZES.caption,
      marginLeft: 0,
    },
  },

  // Select/Dropdown styling
  select: {
    backgroundColor: COLORS.background.paper,
    borderRadius: 1.5,
    fontSize: FONT_SIZES.body1,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.border.light,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.border.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.primary.main,
      borderWidth: 2,
    },
  },

  // Input label (above field)
  inputLabel: {
    fontSize: FONT_SIZES.caption,
    fontWeight: FONT_WEIGHTS.medium,
    color: COLORS.text.secondary,
    mb: 0.75,
    display: 'block',
  },

  // Required asterisk
  requiredMark: {
    color: COLORS.error.main,
    ml: 0.25,
  },
};

// ===========================================
// TOGGLE/SWITCH STYLES
// ===========================================
export const SWITCH_STYLES = {
  // Container for switch with label
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },

  // Switch styling
  switch: {
    '& .MuiSwitch-switchBase': {
      '&.Mui-checked': {
        color: COLORS.background.paper,
        '& + .MuiSwitch-track': {
          backgroundColor: COLORS.primary.main,
          opacity: 1,
        },
      },
    },
    '& .MuiSwitch-track': {
      backgroundColor: COLORS.border.main,
      opacity: 1,
      borderRadius: 12,
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    },
  },

  // Label next to switch
  label: {
    fontSize: FONT_SIZES.body2,
    color: COLORS.text.primary,
    fontWeight: FONT_WEIGHTS.regular,
  },
};

// ===========================================
// FORM FIELD WRAPPER COMPONENT STYLES
// ===========================================
export const FIELD_WRAPPER_STYLES = {
  // Single field wrapper (responsive)
  field: {
    flex: 1,
    minWidth: { xs: '100%', sm: 200 },
  },

  // Field with fixed width (responsive)
  fieldFixed: width => ({
    width: { xs: '100%', sm: width },
    flexShrink: { xs: 1, sm: 0 },
  }),

  // Full width field
  fieldFull: {
    width: '100%',
  },
};

// ===========================================
// PAGE HEADER STYLES (for pipeline setup pages)
// ===========================================
export const PAGE_HEADER_STYLES = {
  // Main header bar
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    px: 3,
    py: 2,
    borderBottom: `1px solid ${COLORS.border.light}`,
    backgroundColor: COLORS.background.paper,
  },

  // Back button + title section
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },

  // Back arrow icon
  backButton: {
    color: COLORS.text.primary,
    '&:hover': {
      backgroundColor: COLORS.background.subtle,
    },
  },

  // Page title (e.g., "Untitled Pipeline")
  title: {
    fontSize: FONT_SIZES.h4,
    fontWeight: FONT_WEIGHTS.medium,
    color: COLORS.text.primary,
  },

  // Actions section (Save Draft, Publish buttons)
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
  },

  // Save Draft button (outlined)
  saveButton: {
    borderColor: COLORS.border.main,
    color: COLORS.text.primary,
    textTransform: 'none',
    fontWeight: FONT_WEIGHTS.medium,
    px: 2.5,
    '&:hover': {
      borderColor: COLORS.text.primary,
      backgroundColor: COLORS.background.subtle,
    },
  },

  // Publish button (contained, disabled state)
  publishButton: {
    textTransform: 'none',
    fontWeight: FONT_WEIGHTS.medium,
    px: 2.5,
  },
};
